import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/popover/Popover'

export interface AvatarData {
  userId: string
  /** Initials to display (1–2 characters recommended) */
  initials: string
  /** Full name — shown in the hover list */
  name: string
  /** Whether this avatar belongs to the logged-in user — appends "(you)" in the list */
  isMe?: boolean
}

export type AvatarStackMaxVisible = 1 | 2 | 3

export interface AvatarStackProps {
  avatars: AvatarData[]
  maxVisible?: AvatarStackMaxVisible
  onClick: () => void
}

const AVATAR_COLORS = ['#2D9F87', '#C0A33A', '#0986D4'] as const

// Bridges the sideOffset gap between the trigger and the popover content — without it,
// moving the pointer across that gap fires onMouseLeave before onMouseEnter can catch it.
const CLOSE_DELAY_MS = 150

export function AvatarStack({ avatars, maxVisible = 3, onClick }: AvatarStackProps) {
  const [showNames, setShowNames] = useState(false)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const visible = avatars.slice(0, maxVisible)
  const overflowCount = avatars.length - visible.length
  const accessibleLabel = avatars.map((a) => a.name).join(', ')

  useEffect(() => () => clearTimeout(closeTimeoutRef.current), [])

  const openNames = () => {
    clearTimeout(closeTimeoutRef.current)
    setShowNames(true)
  }

  const scheduleCloseNames = () => {
    clearTimeout(closeTimeoutRef.current)
    closeTimeoutRef.current = setTimeout(() => setShowNames(false), CLOSE_DELAY_MS)
  }

  if (avatars.length === 0) return null

  return (
    <Popover open={showNames} onOpenChange={setShowNames}>
      <PopoverAnchor asChild>
        <button
          type="button"
          data-ds-component="AvatarStack"
          aria-label={accessibleLabel}
          onClick={onClick}
          onMouseEnter={openNames}
          onMouseLeave={scheduleCloseNames}
          onFocus={openNames}
          onBlur={() => setShowNames(false)}
          className="flex items-center gap-1 cursor-pointer rounded-full pl-0.5 pr-2 py-0.5 hover:bg-surface-hover transition-colors"
        >
          <div className="flex items-center">
            {visible.map((avatar, index) => (
              <div
                key={avatar.userId}
                aria-hidden="true"
                style={{ backgroundColor: AVATAR_COLORS[index % AVATAR_COLORS.length] }}
                className={clsx(
                  'size-6 rounded-full flex items-center justify-center shrink-0 text-white',
                  'outline-offset-0 outline outline-1 outline-white',
                  index < visible.length - 1 && '-mr-1.5'
                )}
              >
                <span className="font-rethink-sans text-[11px] font-semibold leading-none select-none">{avatar.initials}</span>
              </div>
            ))}
          </div>
          {overflowCount > 0 && <span className="text-xs text-text-tertiary">+{overflowCount}</span>}
        </button>
      </PopoverAnchor>
      <PopoverContent
        width="auto"
        showArrow={false}
        align="start"
        className="px-4 py-2 border border-border-default"
        onMouseEnter={openNames}
        onMouseLeave={scheduleCloseNames}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="flex flex-col gap-0.5 max-h-48 overflow-y-auto">
          {avatars.map((avatar) => (
            <p key={avatar.userId} className="text-xs font-medium text-brand-neutral-700 min-h-6 flex items-center whitespace-nowrap">
              {`${avatar.name}${avatar.isMe ? ' (you)' : ''}`}
            </p>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
